import { useContext, useMemo } from 'react';
import MantineTable from '../../../../../common/table/mantine';
import { Button, Checkbox, Text } from '@mantine/core';
import { Status } from '../../../styles';
import moment from 'moment';
import { CostingRequestsContext } from '../../../../../contexts/CostingRequest';
import AssignedUsersList from '../../../../../components/assignedUsersList';

const AvicTable = ({ handleRefresh, data, loading, setLogsModal, getLogsData, fetch }) => {
  const { startManualClosure, info } = useContext(CostingRequestsContext);

  const callLogsData = async (id) => {
    return await getLogsData(id);
  };

  const isNotTP = info.type_of_request !== 'Transfer Pricing';
  const columns = useMemo(() => {
    const baseColumns = [];

    // Conditionally add Retry column
    if (isNotTP) {
      baseColumns.push({
        id: 'action',
        header: 'Action',
        Cell: ({ row }) => {
          return (
            <Button
              disabled={!row?.original?.accessAllowed}
              variant='subtle'
              color='dark'
              size='sm'
              style={{ width: '100%' }}
              onClick={() =>
                handleRefresh(
                  row.original?.distribution_channel,
                  row.original?.condition_type,
                  row.original?.payloadId,
                  row.original?.sales_org,
                )
              }
            >
              Retry
            </Button>
          );
        },
      });
    }

    // Core always-shown columns
    baseColumns.push(
      {
        accessorKey: 'sku',
        header: 'SKU No',
      },
      {
        accessorKey: 'sku_desc',
        header: 'SKU Description',
      },
      {
        accessorKey: 'sales_org',
        header: 'Sales Org',
      },
      {
        accessorKey: 'distribution_channel',
        header: 'Distribution Channel',
      },
      {
        accessorKey: 'condition_type',
        header: 'Condn Type',
      },
      {
        accessorKey: 'key_combination',
        header: 'Key Combn',
      },
      {
        accessorKey: 'costPerHL',
        header: 'Cost/HL',
      },
      {
        accessorKey: 'costPerHundred',
        header: 'Cost/100HL',
      },
      {
        accessorKey: 'currency',
        header: 'Curr',
      },
      {
        accessorKey: 'container_unit',
        header: 'Unit',
      },
      {
        accessorKey: 'uom',
        header: 'Uom',
      },
      {
        accessorKey: 'valid_from',
        header: 'Valid From',
      },
      {
        accessorKey: 'valid_to',
        header: 'Valid To',
      },
      {
        id: 'status',
        header: 'Status',
        Cell: ({ row }) =>
          row.original?.status ? <Status status={row.original?.status} /> : 'N/A',
      },
    );

    // Conditionally add Manual Closure column
    if (isNotTP) {
      baseColumns.push({
        header: 'Action',
        id: 'manually_closed',
        disableSortBy: true,
        Cell: ({ row }) => {
          return ['In Progress', 'Not Started'].includes(row.original.status) ? (
            <Checkbox
              onChange={() => {
                startManualClosure({ open: true, id: row.original.id, callback: fetch });
              }}
              defaultChecked={false}
              label='Confirm and Close'
              disabled={
                ['Closed', 'Not Started'].includes(row.original.status) ||
                !row.original.accessAllowed
              }
            />
          ) : row.original.manually_closed ? (
            <Text>Manual Closure</Text>
          ) : (
            <Text>Automatic Closure</Text>
          );
        },
      });
    }

    // Always-shown columns
    baseColumns.push(
      {
        id: 'logs',
        header: 'Logs',
        Cell: ({ row }) => (
          <Button
            color='dark'
            onClick={async () => {
              const logs = await callLogsData(row.original?.IntCrConditionRecordLogs?.[0]?.id);
              setLogsModal({
                open: true,
                logs: logs,
                type: 'Avic Setup',
              });
            }}
          >
            View Logs
          </Button>
        ),
      },
      {
        accessorKey: 'fpna_gcc_sme',
        header: 'FP&A GCC SME',
        Cell: ({
          row: {
            original: { fpna_gcc_sme },
          },
        }) => <AssignedUsersList users={fpna_gcc_sme} />,
      },
      {
        accessorKey: 'completedOn',
        header: 'Completed On',
      },
    );

    return baseColumns;
  }, [handleRefresh]);

  const initialState = {
    columnPinning: {
      left: ['action', 'sku', 'sku_desc'],
      right: [],
    },
    sorting: [
      {
        id: 'sku_desc',
        desc: false,
      },
    ],
  };
  return (
    <>
      <MantineTable
        columns={columns}
        initialData={data}
        enableRowSelection={false}
        loading={loading}
        showSelectedToggle={false}
        hideSelectColumn={true}
        showResetAll={false}
        pageSize={10}
        initialState={initialState}
        enableColumnFilter={false}
        enableFilters={false}
        enableExport={true}
        columnFilterDisplayMode={false}
        csvFilename={`cost_condtion_creattion_AVIC_${moment().format('YYYY-MM-DD')}`}
      />
    </>
  );
};

export default AvicTable;
